import React, { useState } from "react";
import "./GradeCurricular.css";

// Carrega todos os JSONs da pasta de currículos (webpack)
const ctx = require.context("../../data/API/curriculos", false, /\.json$/);
const curriculos = ctx.keys().map((key) => ctx(key));

const legendaCompleta = {
  PRE: "Atividades realizadas presencialmente na Universidade.",
  EAD: "Atividades desenvolvidas no ambiente virtual de aprendizagem.",
  ALO: "Atividades realizadas ao vivo em ambiente virtual, com horários definidos.",
  EXT: "Atividades acadêmicas em interação com a comunidade.",
  EST: "Estágio/Prática supervisionada com aplicação dos conhecimentos.",
  PRA: "Vivências práticas externas e/ou internas, em locais específicos, ligados à formação profissional.",
  TCC: "Aplicação integrada de conhecimentos e competências acadêmicas.",
};

const tipoSvg = {
  PRE: "https://www.feevale.br/s/conteudo/5fff0851-1f82-415b-b78d-cb09948e3c3a/PRE.svg",
  EAD: "https://www.feevale.br/s/conteudo/57085127-2ffb-44ad-96ec-9dd54c2cd958/EAD.svg",
  ALO: "https://www.feevale.br/s/conteudo/28372a9f-7e9d-4759-ae4f-fa66f7cbc624/ALO.svg",
  EXT: "https://www.feevale.br/s/conteudo/2e26f8df-432c-4536-be53-ce7b015c2774/EXT.svg",
  EST: "https://www.feevale.br/s/conteudo/57dcfd58-f79d-4f31-829a-451f8fb152ce/EST.svg",
  PRA: "https://www.feevale.br/s/conteudo/d051e84e-e062-437a-bd68-f534e510dceb/PRA.svg",
  TCC: "https://www.feevale.br/s/conteudo/0173e1ff-44f3-4636-9770-f7ac14a3ec97/TCC.svg",
};

const ofertaTooltip = {
  TRIMESTRAL: "Disciplina cursada em 10 semanas.",
  SEMESTRAL: "Disciplina cursada em 20 semanas.",
};

function norm(str) {
  return String(str ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function isPeriodoRollOptativas(periodo) {
  const p = norm(periodo);
  return p === "optativas" || p === "optativa";
}

function isModoTrimestral(modo) {
  return norm(modo) === "trimestral";
}

function getPeriodosPorAno(curriculo) {
  const valor = Number(curriculo?.periodos_por_ano);
  if (Number.isFinite(valor) && valor > 0) return valor;
  return isModoTrimestral(curriculo?.modo) ? 4 : 2;
}

function getRotuloPeriodo(curriculo) {
  if (curriculo?.rotulo_periodo) return curriculo.rotulo_periodo;
  return isModoTrimestral(curriculo?.modo) ? "Trimestre" : "Semestre";
}

function getPeriodoTexto(periodo, curriculo) {
  if (isPeriodoRollOptativas(periodo)) return "Optativas";
  return `${periodo} ${getRotuloPeriodo(curriculo)}`;
}

function getNumeroPeriodo(periodo) {
  const match = String(periodo ?? "").match(/\d+/);
  return match ? Number(match[0]) : Number.NaN;
}

function ordenarPeriodos(periodos) {
  return [...periodos].sort((a, b) => {
    const numA = getNumeroPeriodo(a);
    const numB = getNumeroPeriodo(b);

    if (Number.isNaN(numA) && Number.isNaN(numB)) {
      return String(a).localeCompare(String(b), "pt-BR");
    }
    if (Number.isNaN(numA)) return 1;
    if (Number.isNaN(numB)) return -1;
    return numA - numB;
  });
}

function getPeriodosCurriculo(curriculo, filtro = () => true) {
  return ordenarPeriodos(
    [
      ...new Set(
        (curriculo?.componentes ?? [])
          .map((c) => c.periodo)
          .filter((periodo) => periodo && !isPeriodoRollOptativas(periodo))
          .filter(filtro)
      ),
    ]
  );
}

function chunkArray(arr, size) {
  const chunks = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
}

function getAnosFromComponentes(componentes, curriculo) {
  const todosOsPeriodos = ordenarPeriodos(
    [...new Set(componentes.map((c) => c.periodo).filter(Boolean))]
  );

  const periodosRegulares = todosOsPeriodos.filter((p) => !isPeriodoRollOptativas(p));
  const temRollOptativas = todosOsPeriodos.some((p) => isPeriodoRollOptativas(p));
  const periodosPorAno = getPeriodosPorAno(curriculo);
  const anos = [];

  for (let i = 0; i < periodosRegulares.length; i += periodosPorAno) {
    const periodos = periodosRegulares.slice(i, i + periodosPorAno);
    const anoNum = Math.floor(i / periodosPorAno) + 1;
    anos.push({ label: `${anoNum}º Ano`, periodos });
  }

  if (temRollOptativas) {
    anos.push({ label: "Optativas", periodos: ["optativas"] });
  }

  return anos;
}

function Badge({ tipo }) {
  const src = tipoSvg[tipo];
  if (!src) return null;

  return (
    <img
      src={src}
      alt={tipo}
      title={legendaCompleta[tipo] || tipo}
      className="gc-badge-img"
    />
  );
}

// Um componente é do tipo "slot" (placeholder na grade) ou item do roll de optativas.
// Nos dois casos, créditos e oferta não devem aparecer.
function isOcultarOfertaCreditos(c) {
  return c.tipo_slot === true || isPeriodoRollOptativas(c.periodo);
}

function hasCreditos(itens) {
  return itens.some((c) => {
    if (isOcultarOfertaCreditos(c)) return false;
    const val = c.credito ?? c.creditos;
    return val !== null && val !== undefined && String(val).trim() !== "";
  });
}

function hasOferta(itens) {
  return itens.some((c) => {
    if (isOcultarOfertaCreditos(c)) return false;
    return !!c.oferta;
  });
}

function getTotalColumns(exibirCreditos, exibirOferta) {
  let total = 3; // componente, CH, tipo de aula
  if (exibirCreditos) total += 1;
  if (exibirOferta) total += 1;
  return total;
}

function getOfertaClass(oferta) {
  return `gc-oferta gc-oferta--${norm(oferta)}`;
}

function formatOferta(oferta) {
  return String(oferta ?? "").charAt(0) + String(oferta ?? "").slice(1).toLowerCase();
}

function RowComponente({ c, index, exibirCreditos, exibirOferta }) {
  const ocultarEste = isOcultarOfertaCreditos(c);

  return (
    <tr className={index % 2 === 1 ? "gc-tr gc-tr--alt" : "gc-tr"}>
      <td className="gc-td gc-td--nome">
        {c.nome_componente_curricular ?? c.nome}
      </td>

      <td className="gc-td">{c.ch_total ?? c.ch}h</td>

      {exibirCreditos && (
        <td className="gc-td">
          {ocultarEste ? "" : c.credito ?? c.creditos}
        </td>
      )}

      {exibirOferta && (
        <td className="gc-td">
          {ocultarEste || !c.oferta ? (
            ""
          ) : (
            <span
              className={getOfertaClass(c.oferta)}
              data-tooltip={ofertaTooltip[c.oferta]}
            >
              {formatOferta(c.oferta)}
            </span>
          )}
        </td>
      )}

      <td className="gc-td">
        <div className="gc-badges">
          {(c.tipo_aula ?? c.tipos ?? []).map((t) => (
            <Badge key={t} tipo={t} />
          ))}
        </div>
      </td>
    </tr>
  );
}

function TableHeader({ exibirCreditos, exibirOferta }) {
  return (
    <thead>
      <tr>
        <th className="gc-th gc-th--nome">Componente curricular</th>
        <th className="gc-th">CH total</th>
        {exibirCreditos && <th className="gc-th">Créditos</th>}
        {exibirOferta && <th className="gc-th">Oferta</th>}
        <th className="gc-th">Tipo de aula</th>
      </tr>
    </thead>
  );
}

function TabelaComponentes({ itens, exibirCreditos, exibirOferta, mostrarModulo = false }) {
  let moduloAtual = null;
  let itemIndex = 0;
  const totalColumns = getTotalColumns(exibirCreditos, exibirOferta);

  return (
    <div className="gc-table-wrap">
      <div className="gc-table-scroll">
        <table className="gc-table">
          <TableHeader exibirCreditos={exibirCreditos} exibirOferta={exibirOferta} />

          <tbody>
            {itens.map((c, i) => {
              const linhas = [];
              const modulo = c.modulo || c.grupo_optativa;

              if (mostrarModulo && modulo && modulo !== moduloAtual) {
                moduloAtual = modulo;
                linhas.push(
                  <tr key={`modulo-${modulo}-${i}`} className="gc-module-row">
                    <td colSpan={totalColumns} className="gc-module-cell">
                      {modulo}
                    </td>
                  </tr>
                );
              }

              linhas.push(
                <RowComponente
                  key={`comp-${i}`}
                  c={c}
                  index={itemIndex}
                  exibirCreditos={exibirCreditos}
                  exibirOferta={exibirOferta}
                />
              );
              itemIndex += 1;

              return linhas;
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function agruparPorModulo(itens) {
  return itens.reduce((acc, item) => {
    const grupo = item.modulo || item.grupo_optativa || "Optativas";
    if (!acc[grupo]) acc[grupo] = [];
    acc[grupo].push(item);
    return acc;
  }, {});
}

function SemestreTable({ periodo, componentes, curriculo }) {
  const itens = componentes.filter((c) => c.periodo === periodo);
  if (itens.length === 0) return null;

  const exibirCreditos = hasCreditos(itens);
  const exibirOferta = hasOferta(itens);
  const temModulo = itens.some((c) => !!c.modulo || !!c.grupo_optativa);
  const ehRollOptativas = isPeriodoRollOptativas(periodo);

  if (ehRollOptativas && temModulo) {
    const grupos = agruparPorModulo(itens);

    return (
      <div className="gc-sem-block gc-sem-block--optativas">
        <p className="gc-sem-label">{getPeriodoTexto(periodo, curriculo)}</p>

        <div className="gc-optative-groups">
          {Object.entries(grupos).map(([grupo, componentesGrupo]) => {
            const exibirCreditosGrupo = hasCreditos(componentesGrupo);
            const exibirOfertaGrupo = hasOferta(componentesGrupo);

            return (
              <div key={grupo} className="gc-optative-group">
                <p className="gc-module-label">{grupo}</p>
                <TabelaComponentes
                  itens={componentesGrupo}
                  exibirCreditos={exibirCreditosGrupo}
                  exibirOferta={exibirOfertaGrupo}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="gc-sem-block">
      <p className="gc-sem-label">{getPeriodoTexto(periodo, curriculo)}</p>

      <TabelaComponentes
        itens={itens}
        exibirCreditos={exibirCreditos}
        exibirOferta={exibirOferta}
        mostrarModulo={temModulo && !ehRollOptativas}
      />
    </div>
  );
}

function GradePorCurriculo({ curriculo }) {
  const [anoAtivo, setAnoAtivo] = useState(0);
  const anos = getAnosFromComponentes(curriculo.componentes, curriculo);

  return (
    <div className="gc-curriculo-block">
      <p className="gc-curso-label">{curriculo.nome}</p>
      <div>Duração: {curriculo.duracao}</div>
      <p>Integralização curricular: {curriculo.integralizacao}</p>

      <div className="gc-tabs" role="tablist" aria-label="Selecionar ano">
        {anos.map((ano, i) => (
          <button
            key={i}
            role="tab"
            aria-selected={anoAtivo === i}
            className={`gc-tab-btn${anoAtivo === i ? " gc-tab-btn--active" : ""}`}
            onClick={() => setAnoAtivo(i)}
          >
            {ano.label}
          </button>
        ))}
      </div>

      <div className="gc-year-panel">
        {anos[anoAtivo]?.periodos.map((periodo) => (
          <SemestreTable
            key={periodo}
            periodo={periodo}
            componentes={curriculo.componentes}
            curriculo={curriculo}
          />
        ))}
      </div>

      {curriculo.link && (
        <p className="gc-download-link">
          Baixe o currículo completo do curso{" "}
          <a href={curriculo.link} target="_blank" rel="noopener noreferrer" className="gc-download-anchor">
            aqui.
          </a>
        </p>
      )}
    </div>
  );
}

function isCurriculoPsicologia(curriculo) {
  return norm(curriculo?.curso) === "psicologia";
}

function getCurriculoNucleoComum(curriculosPsicologia) {
  return (
    curriculosPsicologia.find((c) => norm(c.nome).includes("nucleo comum")) ||
    curriculosPsicologia.find((c) => getPeriodosCurriculo(c).every((p) => getNumeroPeriodo(p) <= 6)) ||
    null
  );
}

function getHabilitacaoLabel(curriculo) {
  const nome = norm(curriculo?.nome);

  if (nome.includes("institucionais")) {
    return "ÊNFASE EM PROCESSOS INSTITUCIONAIS DE INTERVENÇÃO";
  }

  if (nome.includes("psicoterapicos") || nome.includes("psicoterapicas")) {
    return "ÊNFASE EM PROCESSOS PSICOTERÁPICOS DE INTERVENÇÃO";
  }

  return curriculo?.nome || "Habilitação";
}

function ordenarHabilitacoesPsicologia(habilitacoes) {
  return [...habilitacoes].sort((a, b) => {
    const labelA = getHabilitacaoLabel(a);
    const labelB = getHabilitacaoLabel(b);

    const prioridade = (label) => {
      const n = norm(label);
      if (n.includes("institucionais")) return 1;
      if (n.includes("psicoterapicos") || n.includes("psicoterapicas")) return 2;
      return 3;
    };

    return prioridade(labelA) - prioridade(labelB);
  });
}

function getHabilitacoesPsicologia(curriculosPsicologia, nucleoComum) {
  return ordenarHabilitacoesPsicologia(
    curriculosPsicologia.filter((c) => c !== nucleoComum)
  );
}

function isSeminarioPsicologiaOptativa(componente) {
  const nome = norm(componente?.nome_componente_curricular ?? componente?.nome);
  const modulo = norm(componente?.modulo ?? componente?.grupo_optativa);

  return (
    isPeriodoRollOptativas(componente?.periodo) &&
    (nome.includes("seminario em intervencao psicologica") || modulo.includes("seminario"))
  );
}

function isOptativaRegularPsicologia(componente) {
  return isPeriodoRollOptativas(componente?.periodo) && !isSeminarioPsicologiaOptativa(componente);
}

function filtrarComponentesPsicologia(habilitacao, tipoAba) {
  if (tipoAba === "seminarios") {
    return (habilitacao.componentes ?? []).filter((c) =>
      isPeriodoRollOptativas(c.periodo) ? isSeminarioPsicologiaOptativa(c) : true
    );
  }

  if (tipoAba === "optativas") {
    return (habilitacao.componentes ?? []).filter((c) =>
      isPeriodoRollOptativas(c.periodo) ? isOptativaRegularPsicologia(c) : true
    );
  }

  return habilitacao.componentes ?? [];
}

function temSeminariosPsicologia(habilitacoes) {
  return habilitacoes.some((habilitacao) =>
    (habilitacao.componentes ?? []).some(isSeminarioPsicologiaOptativa)
  );
}

function temOptativasRegularesPsicologia(habilitacoes) {
  return habilitacoes.some((habilitacao) =>
    (habilitacao.componentes ?? []).some(isOptativaRegularPsicologia)
  );
}

function getTituloPsicologia(nucleoComum) {
  const partes = String(nucleoComum?.nome || "Currículo Atual")
    .split(" - ")
    .filter((parte) => !norm(parte).includes("nucleo comum"));

  const base = partes.length ? partes.join(" - ") : "Currículo Atual";
  return `${base} - Psicologia`;
}

function montarAnosPsicologia(nucleoComum, habilitacoes) {
  const anos = [];
  const periodosPorAno = getPeriodosPorAno(nucleoComum || habilitacoes[0]);

  const periodosNucleo = getPeriodosCurriculo(
    nucleoComum,
    (periodo) => getNumeroPeriodo(periodo) <= 6
  );

  chunkArray(periodosNucleo, periodosPorAno).forEach((periodos, index) => {
    anos.push({
      label: `${index + 1}º Ano`,
      tipo: "nucleo",
      periodos,
    });
  });

  const periodosHabilitacoes = ordenarPeriodos(
    [
      ...new Set(
        habilitacoes.flatMap((habilitacao) =>
          getPeriodosCurriculo(habilitacao, (periodo) => getNumeroPeriodo(periodo) > 6)
        )
      ),
    ]
  );

  chunkArray(periodosHabilitacoes, periodosPorAno).forEach((periodos, index) => {
    anos.push({
      label: `${anos.length + 1}º Ano`,
      tipo: "habilitacoes",
      periodos,
    });
  });

  if (temSeminariosPsicologia(habilitacoes)) {
    anos.push({
      label: "Seminários",
      tipo: "seminarios",
      periodos: ["optativas"],
    });
  }

  if (temOptativasRegularesPsicologia(habilitacoes)) {
    anos.push({
      label: "Optativas",
      tipo: "optativas",
      periodos: ["optativas"],
    });
  }

  return anos;
}

function PsicologiaTrack({ habilitacao, periodos, tipoAba = "regular" }) {
  const label = getHabilitacaoLabel(habilitacao);
  const isOptativas = tipoAba === "optativas" || tipoAba === "seminarios";
  const periodosParaRenderizar = isOptativas ? ["optativas"] : periodos;
  const componentesFiltrados = filtrarComponentesPsicologia(habilitacao, tipoAba);

  const titulo = (() => {
    if (tipoAba === "seminarios") return `Seminários - ${label}`;
    if (tipoAba === "optativas") return `Optativas - ${label}`;
    return label;
  })();

  return (
    <div className="gc-psico-track">
      <p className="gc-track-label">{titulo}</p>

      <div className="gc-psico-track-tables">
        {periodosParaRenderizar.map((periodo) => (
          <SemestreTable
            key={`${label}-${tipoAba}-${periodo}`}
            periodo={periodo}
            componentes={componentesFiltrados}
            curriculo={habilitacao}
          />
        ))}
      </div>
    </div>
  );
}

function GradePsicologia({ curriculosPsicologia }) {
  const [anoAtivo, setAnoAtivo] = useState(0);
  const nucleoComum = getCurriculoNucleoComum(curriculosPsicologia);
  const habilitacoes = getHabilitacoesPsicologia(curriculosPsicologia, nucleoComum);

  if (!nucleoComum || habilitacoes.length < 2) {
    return curriculosPsicologia.map((curriculo, idx) => (
      <GradePorCurriculo key={idx} curriculo={curriculo} />
    ));
  }

  const anos = montarAnosPsicologia(nucleoComum, habilitacoes);
  const anoSelecionado = anos[anoAtivo];

  return (
    <div className="gc-curriculo-block gc-curriculo-block--psicologia">
      <p className="gc-curso-label">{getTituloPsicologia(nucleoComum)}</p>
      <div>Duração: {nucleoComum.duracao}</div>
      <p>Integralização curricular: {nucleoComum.integralizacao}</p>

    <p>
      <small>
        <i>
          Ao finalizar as unidades curriculares do núcleo comum (final do terceiro ano) o aluno deverá escolher entre as ênfases: ÊNFASE EM PROCESSOS INSTITUCIONAIS DE INTERVENÇÃO ou ÊNFASE EM PROCESSOS PSICOTERÁPICOS  DE INTERVENÇÃO.
        </i>
      </small>
    </p>

      <div className="gc-tabs" role="tablist" aria-label="Selecionar ano">
        {anos.map((ano, i) => (
          <button
            key={i}
            role="tab"
            aria-selected={anoAtivo === i}
            className={`gc-tab-btn${anoAtivo === i ? " gc-tab-btn--active" : ""}`}
            onClick={() => setAnoAtivo(i)}
          >
            {ano.label}
          </button>
        ))}
      </div>

      <div className="gc-year-panel gc-year-panel--psicologia">
        {anoSelecionado?.tipo === "nucleo" && (
          <div className="gc-psico-section">
            <p className="gc-track-label">Núcleo Comum</p>
            {anoSelecionado.periodos.map((periodo) => (
              <SemestreTable
                key={periodo}
                periodo={periodo}
                componentes={nucleoComum.componentes}
                curriculo={nucleoComum}
              />
            ))}
          </div>
        )}

        {anoSelecionado?.tipo === "habilitacoes" && (
          <div className="gc-psico-tracks">
            {habilitacoes.map((habilitacao) => (
              <PsicologiaTrack
                key={habilitacao.nome}
                habilitacao={habilitacao}
                periodos={anoSelecionado.periodos}
              />
            ))}
          </div>
        )}

        {anoSelecionado?.tipo === "seminarios" && (
          <div className="gc-psico-tracks gc-psico-tracks--seminarios">
            {habilitacoes.map((habilitacao) => (
              <PsicologiaTrack
                key={`seminarios-${habilitacao.nome}`}
                habilitacao={habilitacao}
                periodos={anoSelecionado.periodos}
                tipoAba="seminarios"
              />
            ))}
          </div>
        )}

        {anoSelecionado?.tipo === "optativas" && (
          <div className="gc-psico-tracks gc-psico-tracks--optativas">
            {habilitacoes.map((habilitacao) => (
              <PsicologiaTrack
                key={`optativas-${habilitacao.nome}`}
                habilitacao={habilitacao}
                periodos={anoSelecionado.periodos}
                tipoAba="optativas"
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function GradeCurricular({ curso, formato, tipo }) {
  const matches = Object.values(curriculos).filter(
    (c) =>
      norm(c.curso) === norm(curso) &&
      norm(c.formato) === norm(formato) &&
      norm(c.tipo) === norm(tipo)
  );

  const isPsicologia = norm(curso) === "psicologia" || matches.some(isCurriculoPsicologia);

  const info = matches
  .map((c) => c.info)
  .find((info) => info && String(info).trim() !== "");

  return (
    <div className="gc-root">
      {matches.length === 0 ? (
        <p className="gc-indisponivel">Indisponível</p>
      ) : isPsicologia ? (
        <GradePsicologia curriculosPsicologia={matches} />
      ) : (
        matches.map((curriculo, idx) => (
          <GradePorCurriculo key={idx} curriculo={curriculo} />
        ))
      )}

      <div className="gc-legend" aria-label="Legenda dos tipos de aula">
        {Object.entries(legendaCompleta).map(([t, desc]) => (
          <div key={t} className="gc-legend-item">
            <Badge tipo={t} />
            <span className="gc-legend-desc">{desc}</span>
          </div>
        ))}
      </div>
      {info && (
        <>
          <br />
          <small>
            <i>{info}</i>
          </small>
        </>
      )}
    </div>
  );
}